import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, type Location } from "react-router-dom";
import { setUnauthorizedHandler } from "./api";
import { Spinner } from "./components/bits";
import { Sidebar } from "./components/Sidebar";
import { Toasts } from "./components/Toasts";
import { AuthPage } from "./pages/AuthPage";
import { CollectionPage } from "./pages/CollectionPage";
import { ItemModal, ItemPage } from "./pages/ItemPanel";
import { MeetingPage } from "./pages/MeetingPage";
import { MeetingsPage } from "./pages/MeetingsPage";
import { MyTasksPage } from "./pages/MyTasksPage";
import { SpacePage } from "./pages/SpacePage";
import { ConnectionsPage } from "./pages/settings/ConnectionsPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AuditTab } from "./pages/admin/AuditTab";
import { IdentitiesTab } from "./pages/admin/IdentitiesTab";
import { MembersTab } from "./pages/admin/MembersTab";
import { OverviewTab } from "./pages/admin/OverviewTab";
import { SettingsTab } from "./pages/admin/SettingsTab";
import { SyncTab } from "./pages/admin/SyncTab";
import { SettingsLayout } from "./pages/settings/SettingsLayout";
import { checkSession, loadInstance, loadMembers, loadSpaces, setMe, store, toastError } from "./store/app";
import { startEvents, stopEvents } from "./store/events";
import { useApp } from "./store/hooks";

type Boot = "loading" | "ready" | "unreachable";

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [boot, setBoot] = useState<Boot>("loading");
  const me = useApp((s) => s.me);
  const orgId = useApp((s) => s.orgId);
  const instance = useApp((s) => s.instance);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!store.get().me) return;
      setMe(null);
      navigate("/login", { replace: true, state: { from: window.location.pathname } });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inst = await loadInstance();
        if (cancelled) return;
        if (inst.needs_setup) {
          setMe(null);
          setBoot("ready");
          return;
        }
        await checkSession();
        if (!cancelled) setBoot("ready");
      } catch {
        if (cancelled) return;
        setMe(null);
        setBoot("unreachable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!me || !orgId) return;
    loadSpaces().catch((e) => toastError(e, "Could not load spaces"));
    loadMembers().catch(() => undefined);
    startEvents();
    return () => stopEvents();
  }, [me, orgId]);

  if (boot === "loading") {
    return (
      <div className="boot">
        <Spinner />
      </div>
    );
  }

  const state = location.state as { background?: Location } | null;
  const background = state?.background;
  const needsSetup = instance?.needs_setup === true;

  return (
    <>
      {boot === "unreachable" && (
        <div className="banner" role="alert">
          The server is not reachable. Check that the backend is running.
          <button type="button" className="btn-link" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      )}
      <Routes location={background ?? location}>
        <Route
          path="/setup"
          element={needsSetup ? <AuthPage mode="setup" /> : <Navigate to={me ? "/" : "/login"} replace />}
        />
        <Route
          path="/login"
          element={
            needsSetup ? <Navigate to="/setup" replace /> : me ? <Navigate to="/" replace /> : <AuthPage mode="login" />
          }
        />
        <Route path="/register" element={me ? <Navigate to="/" replace /> : <AuthPage mode="register" />} />
        <Route
          element={
            <RequireAuth needsSetup={needsSetup}>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Home />} />
          <Route path="c/:collectionId" element={<CollectionPage />} />
          <Route path="s/:spaceId" element={<SpacePage />} />
          <Route path="items/:id" element={<ItemPage />} />
          <Route path="my-tasks" element={<MyTasksPage />} />
          <Route path="meetings" element={<MeetingsPage />} />
          <Route path="meetings/:id" element={<MeetingPage />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="connections" replace />} />
            <Route path="connections" element={<ConnectionsPage />} />
            <Route path="people" element={<Navigate to="/admin/identities" replace />} />
          </Route>
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<OverviewTab />} />
            <Route path="members" element={<MembersTab />} />
            <Route path="identities" element={<IdentitiesTab />} />
            <Route path="sync" element={<SyncTab />} />
            <Route path="audit" element={<AuditTab />} />
            <Route path="settings" element={<SettingsTab />} />
            <Route path="*" element={<Navigate to="overview" replace />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      {background && me && (
        <Routes>
          <Route path="/items/:id" element={<ItemModal />} />
        </Routes>
      )}
      <Toasts />
    </>
  );
}

function RequireAuth({ children, needsSetup }: { children: ReactNode; needsSetup: boolean }) {
  const me = useApp((s) => s.me);
  const location = useLocation();
  if (needsSetup) return <Navigate to="/setup" replace />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function Layout() {
  const [navOpen, setNavOpen] = useState(false);
  const timezone = useApp((s) => s.timezone);
  const location = useLocation();
  useEffect(() => setNavOpen(false), [location.pathname]);
  return (
    <div className={`shell${navOpen ? " nav-open" : ""}`}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <header className="mobile-bar">
        <button
          type="button"
          className="icon-btn"
          aria-label={navOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((o) => !o)}
        >
          ☰
        </button>
        <span className="mobile-title">ToDoItAll</span>
      </header>
      <Sidebar onNavigate={() => setNavOpen(false)} />
      {navOpen && <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}
      <main id="main" className="main" tabIndex={-1}>
        {/* Re-render dates everywhere when the display time zone changes. */}
        <Outlet key={timezone} />
      </main>
    </div>
  );
}

function Home() {
  const spaces = useApp((s) => s.spaces);
  const currentSpaceId = useApp((s) => s.currentSpaceId);
  const collections = useApp((s) => s.collections);
  if (spaces === null) return <Spinner />;
  const spaceId = currentSpaceId ?? spaces[0]?.id;
  const cols = spaceId ? collections[spaceId] : undefined;
  if (spaceId && cols === undefined) return <Spinner />;
  const first = cols?.find((c) => c.kind !== "notebook") ?? cols?.[0];
  if (first) return <Navigate to={`/c/${first.id}`} replace />;
  return <Navigate to="/my-tasks" replace />;
}

function NotFound() {
  return (
    <div className="page">
      <h1>Not found</h1>
      <p className="muted">This page does not exist.</p>
    </div>
  );
}
